import { cn, ProgressBar } from '@atlas/ui-primitives'
import { Check } from 'lucide-react'
import { useState } from 'react'
import { fileTypeIcon, fileTypeToken, isPreviewable } from '../asset-tile/asset-format'
import type { AssetListAsset } from './asset-list-card.types'
import { tileFormatLabel, tilePlaceholderLabel } from './asset-list-labels'

/**
 * How far into the grid the deal-in keeps staggering. Past a dozen tiles the
 * sequence stops reading as an order and starts reading as a slow page, so every
 * later tile arrives with the twelfth.
 */
const MAX_STAGGER_STEPS = 11
const STAGGER_STEP_MS = 30

export interface AssetListTileProps {
	asset: AssetListAsset
	/** The kit role this tile is listed under - the part of the placeholder label that is always present. */
	roleLabel: string
	/**
	 * Whether the card is selecting. It decides what the tile IS: a link to the asset, or a
	 * toggle for the selection. One tile never offers both.
	 */
	selectionMode: boolean
	selected: boolean
	onToggleSelection: (assetId: string) => void
	onOpen: (assetId: string) => void
	/** Position in its group's grid, which is the tile's place in the deal-in sequence. */
	index?: number
}

/**
 * One tile in an asset list: the asset's thumbnail where one resolved, and a specimen plate
 * identifying the asset otherwise. A font, a brandbook and an unrendered image are picked by
 * what they are FOR, so the plate carries the role, the qualifiers that separate this asset
 * from its siblings in that role, its filename, and its format and size - enough that two
 * repeats of one role are never indistinguishable.
 *
 * The plate is laid out as a catalog card - role at the head, file data at the foot under a
 * hairline, the file-type glyph oversized and cropped into the corner behind both. Left-aligned
 * and on a tinted ground rather than centred on an ink block: a solid black square reads as
 * redacted content at grid scale, and centring three lines of mono gives a set of assets the
 * texture of an error state.
 *
 * The tile has ONE action, decided by whether the card is selecting: it opens the asset, or
 * it toggles that asset's selection. Two controls on one small square - a link plus a
 * permanent checkbox - put a mis-click between every asset and its detail page, and made the
 * grid read as a form rather than as a set of assets. Selecting instead makes the whole tile
 * the target, which is a larger hit area than a checkbox and works the same under a finger.
 */
export function AssetListTile({
	asset,
	roleLabel,
	selectionMode,
	selected,
	onToggleSelection,
	onOpen,
	index = 0,
}: AssetListTileProps) {
	/**
	 * The URL whose image failed to load, if any. A presigned URL can 404 (the thumbnail
	 * object is gone) or expire before the browser fetches it, and a broken image tells the
	 * reader nothing - so a failed load is treated as an unresolved thumbnail and falls back
	 * to the plate. Keyed by URL rather than a flag, so a fresh URL for the same asset is
	 * attempted again instead of inheriting the previous one's failure.
	 */
	const [failedUrl, setFailedUrl] = useState<string | null>(null)
	const { id, fileName, contentType, fileSize, qualifiers, previewUrl, uploadProgress } = asset
	const isUploading = uploadProgress !== undefined
	const showPreview = Boolean(previewUrl) && isPreviewable(contentType) && previewUrl !== failedUrl
	const TypeGlyph = fileTypeIcon(fileTypeToken(contentType, fileName))

	const face = showPreview ? (
		// `contain` on a padded ground, never `cover`. The tile is square and most brand artwork
		// is not: `cover` scaled a wide wordmark up and clipped its sides, so a Bravecto logo read
		// as "AVEC". A grid for RECOGNISING an asset must show all of it - cropping is what the
		// detail page's own preview is for, and the Library's cards already contain.
		<img
			src={previewUrl ?? undefined}
			alt={fileName}
			onError={() => setFailedUrl(previewUrl ?? null)}
			className="h-full w-full object-contain p-ds-3 transition-transform duration-(--ds-motion-duration-base) group-hover:scale-[1.04]"
		/>
	) : (
		// A specimen plate: what the asset is at the head, which file it is at the foot, and the
		// type glyph oversized behind them so a shelf of unrendered assets is still scannable by
		// shape. `break-words` so a long unbroken code or filename wraps instead of bleeding out
		// of the narrowest (two-column) tile.
		<span className="relative flex h-full w-full flex-col justify-between overflow-hidden bg-ds-gradient-soft p-ds-3 text-left">
			<TypeGlyph
				aria-hidden
				strokeWidth={1}
				className="pointer-events-none absolute -bottom-5 -right-4 size-28 text-ds-text opacity-[0.07] transition-transform duration-(--ds-motion-duration-slow) group-hover:-translate-y-1 group-hover:rotate-[-6deg]"
			/>
			<span className="relative break-words font-mono text-ds-2xs uppercase leading-snug tracking-ds-wider text-ds-text">
				{tilePlaceholderLabel(roleLabel, qualifiers)}
			</span>
			<span className="relative">
				<span aria-hidden className="mb-ds-2 block h-px w-full bg-ds-border" />
				{/* The one value that always separates two otherwise identical tiles. Clamped rather
				    than truncated, so a name that differs at its END still shows the difference on
				    the second line. */}
				<span className="line-clamp-2 break-words text-ds-3xs leading-tight text-ds-text-muted">{fileName}</span>
				<span className="mt-ds-0.5 block font-mono text-ds-3xs leading-tight tracking-ds-wide text-ds-text-subtle">
					{tileFormatLabel(contentType, fileName, fileSize)}
				</span>
			</span>
		</span>
	)

	const faceClassName =
		'flex aspect-square w-full items-center justify-center overflow-hidden outline-none focus-visible:ring-2 focus-visible:ring-ds-ring'

	return (
		<div
			data-selected={selected}
			style={{ animationDelay: `${Math.min(index, MAX_STAGGER_STEPS) * STAGGER_STEP_MS}ms` }}
			className={cn(
				'group relative animate-ds-deal-in overflow-hidden rounded-ds-base border border-ds-border bg-ds-surface-elevated',
				'transition-[transform,translate,box-shadow,border-color] duration-(--ds-motion-duration-fast)',
				'hover:-translate-y-0.5 hover:border-ds-border-strong hover:shadow-ds-lg',
				'data-[selected=true]:border-ds-primary data-[selected=true]:ring-2 data-[selected=true]:ring-ds-primary',
			)}
		>
			{isUploading ? (
				// Nothing to open and nothing to select: the file is still being sent. The tile
				// states it and shows how far it has got.
				<div aria-busy="true" aria-label={fileName} title={fileName} className={cn(faceClassName, 'opacity-60')}>
					{face}
				</div>
			) : selectionMode ? (
				// `role="checkbox"` rather than a pressed button: this is one independent choice
				// among many, which is what a screen reader needs to hear here - and it keeps the
				// tile and the group's own select-all the same kind of control.
				<button
					type="button"
					role="checkbox"
					aria-checked={selected}
					aria-label={`Select ${fileName}`}
					title={fileName}
					onClick={() => onToggleSelection(id)}
					className={faceClassName}
				>
					{face}
				</button>
			) : (
				<button
					type="button"
					onClick={() => onOpen(id)}
					aria-label={fileName}
					title={fileName}
					className={faceClassName}
				>
					{face}
				</button>
			)}
			{/* The rail sits ON the tile rather than under it, so an import occupies exactly the
			    cell it will end up in and the grid does not reflow when it finishes. No plate
			    behind it: the dimmed face already reads as "not here yet", and a second surface
			    over it only added an edge. */}
			{isUploading ? (
				<div className="absolute inset-x-0 bottom-0 p-1.5">
					<ProgressBar size="sm" value={uploadProgress} aria-label={`Upload progress for ${fileName}`} />
				</div>
			) : null}
			{/* A thumbnail identifies the artwork but not WHICH file it is, and hovering for a
			    title tooltip is not reading. The name rides up over the image on hover or keyboard
			    focus, so the grid stays artwork until the reader asks. */}
			{showPreview && !isUploading ? (
				<span
					aria-hidden
					className="pointer-events-none absolute inset-x-0 bottom-0 translate-y-full break-words bg-ds-text/85 px-ds-2 py-ds-1 text-ds-3xs leading-tight text-ds-background transition-transform duration-(--ds-motion-duration-fast) group-hover:translate-y-0 group-focus-within:translate-y-0"
				>
					<span className="line-clamp-2">{fileName}</span>
				</span>
			) : null}
			{/* Shown only when selected, and only while selecting - the state needs a mark that
			    survives over artwork of any colour, but an empty box on every tile is the clutter
			    this replaced. */}
			{selectionMode && selected ? (
				<span
					aria-hidden
					className="absolute right-1.5 top-1.5 flex size-5 animate-ds-deal-in items-center justify-center rounded-ds-full bg-ds-primary text-ds-primary-foreground shadow-ds-sm"
				>
					<Check className="size-3.5" />
				</span>
			) : null}
		</div>
	)
}
